import { Accordion } from "@/components/ui/accordion";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { ROUTES } from "@/constants/routes";
import { useOpenSettings } from "@/context/OpenSettingsContext";
import { RailFavoritesDropTarget } from "@/dnd";
import { ConnectorsRailDraggableConnectorRow } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/connectors/ConnectorsRailDraggableConnectorRow";
import { useSelectableIntegrationConnections } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/connectors/useSelectableIntegrationConnections";
import { RailGroup } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailGroup";
import { RailGroupActionButton } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailGroupActionButton";
import {
  RailRowMenu,
  railRowMenuProps,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailRowMenu";
import { RailZeroState } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailZeroState";
import { railZeroStateCopy } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/railZeroStateCopy";
import { useRailRowContextMenu } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/useRailRowContextMenu";
import { RESOURCE_TAG_FAVORITE } from "@cohesive/api-client";
import { Blocks, Plus, Star } from "lucide-react";

export function ConnectorsRail() {
  const openSettings = useOpenSettings();
  const addConnectorMenu = useRailRowContextMenu();
  const { connections, isError } = useSelectableIntegrationConnections();

  if (isError) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center px-4 py-8">
        <p className="text-center text-sm text-n-40">
          Couldn&apos;t load connectors.
        </p>
      </div>
    );
  }

  // Favorites are derived client-side from the already-loaded connections.
  const favoriteRows = connections
    .filter((connection) => connection.tags?.includes(RESOURCE_TAG_FAVORITE))
    .map((connection) => (
      <ConnectorsRailDraggableConnectorRow
        key={connection.id}
        connection={connection}
        dragNamespace="favorites"
      />
    ));

  const connectorRows = connections.map((connection) => (
    <ConnectorsRailDraggableConnectorRow
      key={connection.id}
      connection={connection}
      dragNamespace="all"
    />
  ));

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-2 py-2">
      <Accordion
        type="multiple"
        defaultValue={["connectors-favorites", "connectors-all"]}
        className="flex flex-col"
      >
        <RailFavoritesDropTarget resourceType="integration-connection">
          <RailGroup
            groupId="connectors-favorites"
            label="Starred"
            icon={Star}
            noCap
            items={favoriteRows}
            emptyState={
              <RailZeroState
                icon={Star}
                variant="passive"
                title="Pin what matters."
                description="Star a connector and it stays here."
              />
            }
          />
        </RailFavoritesDropTarget>
        <RailGroup
          groupId="connectors-all"
          label="Connectors"
          icon={Blocks}
          noCap
          onContextMenu={addConnectorMenu.onContextMenu}
          rightSlot={
            <RailGroupActionButton
              label="Add connector"
              onClick={() => openSettings(ROUTES.SETTINGS.CONNECTORS)}
            />
          }
          items={connectorRows}
          emptyState={
            <RailZeroState
              icon={Blocks}
              accent="plum"
              title={railZeroStateCopy.connectors.all.title}
              description={railZeroStateCopy.connectors.all.description}
              action={{
                label: railZeroStateCopy.connectors.all.cta,
                onClick: () => openSettings(ROUTES.SETTINGS.CONNECTORS),
                icon: Plus,
              }}
            />
          }
        />
      </Accordion>

      <RailRowMenu
        {...railRowMenuProps(addConnectorMenu)}
        kebab={false}
        label="Connectors"
      >
        <DropdownMenuItem
          onClick={() => openSettings(ROUTES.SETTINGS.CONNECTORS)}
        >
          <Plus />
          Add connector
        </DropdownMenuItem>
      </RailRowMenu>
    </div>
  );
}
